import { useMemo } from 'react'

import { useGisEntities } from '../../../hooks/useGisEntities'
import { prettyName } from '../../../lib/mandal-name'
import { FilterDropdown, MultiFilterDropdown, OptionRow } from '../../ui/FilterDropdown'
import { FilterChipBar } from '../FilterChipBar'

import type { GisEntity, MandalAggregate } from '../../../api/types'

interface DashboardToolbarProps {
  mandals: MandalAggregate[]
  activeMandals: readonly string[]
  onSelectMandal: (name: string) => void
  onClearMandal: () => void
  /** URL-backed `?entityId=` — the server re-scopes every dashboard query (issues, sites, trends). */
  entityId: string | undefined
  onEntityChange: (id: string | undefined) => void
}

/** Single-select entity scope as one of the standard filter dropdowns (radio rows + site counts). */
function EntityDropdown({ entities, entityId, onEntityChange }: {
  entities: GisEntity[] | undefined
  entityId: string | undefined
  onEntityChange: (id: string | undefined) => void
}) {
  const current = entities?.find((e) => e.id === entityId)
  const totalSites = (entities ?? []).reduce((a, e) => a + e.sitesCount, 0)
  return (
    <FilterDropdown label="Entity" {...(current ? { valueText: current.name } : {})}>
      <OptionRow
        radio
        checked={entityId === undefined}
        label="All entities"
        count={totalSites}
        onToggle={() => onEntityChange(undefined)}
      />
      {(entities ?? []).map((e) => (
        <OptionRow
          key={e.id}
          radio
          checked={e.id === entityId}
          label={e.name}
          count={e.sitesCount}
          onToggle={() => onEntityChange(e.id)}
        />
      ))}
    </FilterDropdown>
  )
}

/**
 * The Dashboard tab's control row: the two scopes that truthfully re-cut EVERY module — the mandal
 * multi-select (client-side, joined via sites) and the entity single-select (server-side, on every
 * query). The period lives in the tab bar; every deeper cut is one click away on the boards.
 */
export function DashboardToolbar({ mandals, activeMandals, onSelectMandal, onClearMandal, entityId, onEntityChange }: DashboardToolbarProps) {
  const { data: entities } = useGisEntities()
  const options = useMemo(
    () =>
      mandals
        .map((m) => ({ value: m.name, label: prettyName(m.name), count: m.siteCount }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    [mandals],
  )
  const entityName = entities?.find((e) => e.id === entityId)?.name
  const chips = [
    ...activeMandals.map((m) => ({ label: prettyName(m), clear: () => onSelectMandal(m) })),
    ...(entityId !== undefined
      ? [{ label: `Entity: ${entityName ?? entityId}`, clear: () => onEntityChange(undefined) }]
      : []),
  ]
  const clearAll = (): void => {
    onClearMandal()
    onEntityChange(undefined)
  }

  return (
    <div className="border-b border-gis-line bg-white">
      <div className="flex flex-wrap items-center gap-2 px-3.5 py-[7px]">
        <MultiFilterDropdown label="Mandal" options={options} active={activeMandals} onToggle={onSelectMandal} />
        <EntityDropdown entities={entities} entityId={entityId} onEntityChange={onEntityChange} />
        <span className="text-[11px] text-gis-muted">
          {chips.length === 0
            ? 'District-wide · every tile, chart and row is a shortcut into the boards'
            : 'Scoped — every module reflects the selected filters'}
        </span>
      </div>
      {chips.length > 0 && (
        <div className="border-t border-gis-line-2 px-3.5 py-[5px]">
          <FilterChipBar chips={chips} onClearAll={clearAll} />
        </div>
      )}
    </div>
  )
}
